import * as THREE from 'three';

type Part = { geometry: THREE.BufferGeometry; material: THREE.Material };

/**
 * Procedural low-poly humanoid. The root sits at the feet and faces -Z.
 * Limbs are pivot groups so walking is a few rotations per frame.
 */
export class Figure {
  readonly root = new THREE.Group();
  readonly body = new THREE.Group();
  readonly legL = new THREE.Group();
  readonly legR = new THREE.Group();
  readonly armL = new THREE.Group();
  readonly armR = new THREE.Group();
  readonly head = new THREE.Group();
  readonly propHand = new THREE.Group();
  private readonly owned: Part[] = [];
  private readonly materials = new Map<string, THREE.Material>();

  constructor() {
    this.root.add(this.body);
  }

  material(key: string, make: () => THREE.Material): THREE.Material {
    let m = this.materials.get(key);
    if (!m) {
      m = make();
      this.materials.set(key, m);
    }
    return m;
  }

  std(color: THREE.ColorRepresentation, roughness = 0.85): THREE.Material {
    return this.material(`std:${new THREE.Color(color).getHexString()}:${roughness}`, () =>
      new THREE.MeshStandardMaterial({ color, roughness }),
    );
  }

  mesh(
    parent: THREE.Object3D,
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    x: number,
    y: number,
    z: number,
    sx = 1,
    sy = 1,
    sz = 1,
  ): THREE.Mesh {
    const m = new THREE.Mesh(geometry, material);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    m.castShadow = true;
    m.receiveShadow = false;
    parent.add(m);
    this.owned.push({ geometry, material });
    return m;
  }

  /**
   * @param phase  gait phase in radians
   * @param stride 0 = standing, 1 = full walking stride
   * @param run    0..1 blend toward a running pose
   */
  pose(phase: number, stride: number, run: number, armSwing: number, headYaw: number, headPitch = 0): void {
    const s = Math.sin(phase);
    const legAmp = (0.5 + run * 0.45) * stride;
    this.legL.rotation.x = s * legAmp;
    this.legR.rotation.x = -s * legAmp;
    const armAmp = armSwing * (0.45 + run * 0.5) * stride;
    this.armL.rotation.x = -s * armAmp;
    this.armR.rotation.x = s * armAmp;
    this.body.position.y = Math.abs(Math.cos(phase)) * 0.035 * stride * (1 + run);
    this.body.rotation.x = -0.05 * stride - run * 0.2;
    this.body.rotation.y = s * 0.05 * stride;
    this.head.rotation.y = headYaw - this.body.rotation.y;
    this.head.rotation.x = headPitch + run * 0.12;
  }

  dispose(): void {
    const geos = new Set<THREE.BufferGeometry>();
    for (const p of this.owned) geos.add(p.geometry);
    for (const g of geos) g.dispose();
    for (const m of this.materials.values()) m.dispose();
  }
}

const capsule = (r: number, len: number) => new THREE.CapsuleGeometry(r, len, 4, 10);

export function createHeroineFigure(): { figure: Figure; phoneScreen: THREE.MeshBasicMaterial } {
  const f = new Figure();
  const skin = f.std('#e6c3a6', 0.7);
  const hair = f.std('#0d0b0c', 0.5);
  const coat = f.std('#b9a689', 0.8);
  const skirt = f.std('#2e3247', 0.9);
  const tights = f.std('#1b1a1f', 0.6);
  const shoe = f.std('#0e0e10', 0.4);
  const scarf = f.std('#8a2d38', 0.9);
  const bag = f.std('#4a3022', 0.6);

  const hipY = 0.82;
  f.body.position.y = 0;

  for (const [leg, x] of [[f.legL, -0.085], [f.legR, 0.085]] as const) {
    leg.position.set(x, hipY, 0);
    f.body.add(leg);
    f.mesh(leg, capsule(0.058, 0.66), tights, 0, -0.39, 0);
    f.mesh(leg, new THREE.BoxGeometry(0.09, 0.07, 0.2), shoe, 0, -0.79, -0.04);
  }

  // Pleated skirt and coat
  f.mesh(f.body, new THREE.CylinderGeometry(0.16, 0.25, 0.36, 14), skirt, 0, hipY - 0.12, 0);
  f.mesh(f.body, new THREE.CylinderGeometry(0.165, 0.2, 0.56, 14), coat, 0, hipY + 0.24, 0);
  f.mesh(f.body, new THREE.SphereGeometry(0.17, 14, 10), coat, 0, hipY + 0.5, 0, 1.12, 0.6, 0.9);
  f.mesh(f.body, new THREE.TorusGeometry(0.075, 0.035, 8, 16), scarf, 0, hipY + 0.6, 0, 1, 1, 1).rotation.x = Math.PI / 2;
  // Scarf tail
  f.mesh(f.body, new THREE.BoxGeometry(0.07, 0.24, 0.03), scarf, 0.05, hipY + 0.47, -0.16);

  // Head
  f.head.position.set(0, hipY + 0.64, 0);
  f.body.add(f.head);
  f.mesh(f.head, new THREE.CylinderGeometry(0.035, 0.04, 0.08, 8), skin, 0, 0.02, 0);
  f.mesh(f.head, new THREE.SphereGeometry(0.1, 16, 12), skin, 0, 0.13, -0.005, 0.92, 1.05, 1);
  f.mesh(f.head, new THREE.SphereGeometry(0.112, 16, 12), hair, 0, 0.16, 0.014, 1, 0.98, 1.02);
  const bob = new THREE.CylinderGeometry(0.112, 0.125, 0.19, 18, 1, true, Math.PI + 0.95, Math.PI * 2 - 1.9);
  f.mesh(f.head, bob, f.material('hairDouble', () => new THREE.MeshStandardMaterial({ color: '#0d0b0c', roughness: 0.5, side: THREE.DoubleSide })), 0, 0.085, 0.012);
  f.mesh(f.head, new THREE.BoxGeometry(0.17, 0.05, 0.05), hair, 0, 0.2, -0.085);

  // Arms
  for (const [arm, x] of [[f.armL, -0.215], [f.armR, 0.215]] as const) {
    arm.position.set(x, hipY + 0.5, 0);
    f.body.add(arm);
    f.mesh(arm, capsule(0.045, 0.44), coat, 0, -0.26, 0);
    f.mesh(arm, new THREE.SphereGeometry(0.04, 8, 6), skin, 0, -0.53, 0);
  }

  // Shoulder bag
  f.mesh(f.body, new THREE.BoxGeometry(0.08, 0.2, 0.26), bag, 0.25, hipY + 0.02, 0.02);
  const strap = f.mesh(f.body, new THREE.BoxGeometry(0.025, 0.62, 0.02), bag, 0.06, hipY + 0.33, -0.165);
  strap.rotation.z = -0.62;

  // Phone held in the right hand (shown while checking the map)
  const phoneScreen = new THREE.MeshBasicMaterial({ color: '#cfe4ff' });
  phoneScreen.color.multiplyScalar(1.1);
  f.propHand.position.set(0, -0.55, -0.04);
  f.armR.add(f.propHand);
  f.mesh(f.propHand, new THREE.BoxGeometry(0.075, 0.15, 0.012), f.std('#111'), 0, 0, 0);
  const screen = f.mesh(f.propHand, new THREE.PlaneGeometry(0.066, 0.13), phoneScreen, 0, 0, 0.007);
  screen.castShadow = false;
  f.propHand.rotation.x = -Math.PI / 2;
  f.propHand.visible = false;

  return { figure: f, phoneScreen };
}

export function createStrangerFigure(): { figure: Figure; phoneScreen: THREE.MeshBasicMaterial } {
  const f = new Figure();
  const coat = f.std('#15171b', 0.9);
  const pants = f.std('#101114', 0.85);
  const shoe = f.std('#08080a', 0.5);
  const skin = f.std('#9c8574', 0.8);
  const cap = f.std('#0b0c0e', 0.7);
  const mask = f.std('#a9aaa6', 0.95);

  const hipY = 0.96;
  for (const [leg, x] of [[f.legL, -0.11], [f.legR, 0.11]] as const) {
    leg.position.set(x, hipY, 0);
    f.body.add(leg);
    f.mesh(leg, capsule(0.075, 0.78), pants, 0, -0.46, 0);
    f.mesh(leg, new THREE.BoxGeometry(0.12, 0.08, 0.27), shoe, 0, -0.92, -0.05);
  }
  // Long dark coat
  f.mesh(f.body, new THREE.CylinderGeometry(0.22, 0.3, 0.95, 14), coat, 0, hipY + 0.12, 0);
  f.mesh(f.body, new THREE.SphereGeometry(0.24, 14, 10), coat, 0, hipY + 0.6, 0, 1.15, 0.55, 0.85);
  f.mesh(f.body, new THREE.CylinderGeometry(0.1, 0.13, 0.12, 10), coat, 0, hipY + 0.7, 0);

  f.head.position.set(0, hipY + 0.74, 0);
  f.body.add(f.head);
  f.mesh(f.head, new THREE.SphereGeometry(0.115, 14, 10), skin, 0, 0.13, 0, 0.92, 1.05, 1);
  f.mesh(f.head, new THREE.SphereGeometry(0.122, 14, 10), cap, 0, 0.18, 0.01, 1, 0.75, 1);
  const brim = f.mesh(f.head, new THREE.CylinderGeometry(0.1, 0.1, 0.015, 12), cap, 0, 0.15, -0.12, 1, 1, 0.75);
  brim.rotation.x = 0.15;
  // White face mask — common, ordinary, and it hides his face.
  f.mesh(f.head, new THREE.BoxGeometry(0.15, 0.08, 0.05), mask, 0, 0.09, -0.095);

  for (const [arm, x] of [[f.armL, -0.27], [f.armR, 0.27]] as const) {
    arm.position.set(x, hipY + 0.6, 0);
    f.body.add(arm);
    f.mesh(arm, capsule(0.062, 0.5), coat, 0, -0.3, 0);
  }
  // Hands in pockets: arms angled slightly in and forward.
  f.armL.rotation.z = -0.1;
  f.armR.rotation.z = 0.1;

  // His screen glows red: a warning colour, and it never reads as her phone.
  const phoneScreen = new THREE.MeshBasicMaterial({ color: '#ff3a2e' });
  phoneScreen.color.multiplyScalar(2);
  f.propHand.position.set(0, -0.58, -0.05);
  f.armR.add(f.propHand);
  f.mesh(f.propHand, new THREE.BoxGeometry(0.08, 0.16, 0.012), f.std('#111'), 0, 0, 0);
  const screen = f.mesh(f.propHand, new THREE.PlaneGeometry(0.07, 0.14), phoneScreen, 0, 0, 0.007);
  screen.castShadow = false;
  f.propHand.rotation.x = -Math.PI / 2;
  f.propHand.visible = false;

  return { figure: f, phoneScreen };
}
